import React from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isBookForPawsAdmin } from '../utils/adminUser';
import AdminLayout from './AdminLayout';

/**
 * Admin UI requires sign-in and an administrator profile (role / isAdmin).
 * Profile is loaded once on app init via AuthProvider — do not refresh here (caused request loops).
 */
const AdminGate = () => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-6">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
          <h1 className="text-xl font-medium text-black mb-2">Sign in to use admin</h1>
          <p className="text-[14px] text-[#8c8c8c] leading-relaxed mb-6">
            Image uploads and other admin actions need your BookForPaws account. Log in with your phone (OTP) on
            Account, then open admin again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/account"
              className="inline-flex justify-center items-center rounded-full bg-[#FF5E4C] text-white text-[14px] font-medium px-6 py-3 hover:opacity-90 transition-opacity"
            >
              Go to Account
            </Link>
            <Link
              to="/"
              className="inline-flex justify-center items-center rounded-full border border-gray-300 text-black text-[14px] font-medium px-6 py-3 hover:bg-gray-50 transition-colors"
            >
              Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!isBookForPawsAdmin(user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-6">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
          <h1 className="text-xl font-medium text-black mb-2">Administrator access required</h1>
          <p className="text-[14px] text-[#8c8c8c] leading-relaxed mb-6">
            You are signed in as <span className="text-black font-medium">{user?.mobile || user?.name || 'this account'}</span>
            , but it does not have admin access. Log in via <strong>Account</strong> (main site OTP, not vendor login)
            after your profile has <span className="text-black font-medium">role: admin</span> and/or{' '}
            <span className="text-black font-medium">isAdmin: true</span> in the database, then open{' '}
            <span className="text-black font-medium">/admin</span> again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/account"
              className="inline-flex justify-center items-center rounded-full bg-[#FF5E4C] text-white text-[14px] font-medium px-6 py-3 hover:opacity-90 transition-opacity"
            >
              Account
            </Link>
            <Link
              to="/"
              className="inline-flex justify-center items-center rounded-full border border-gray-300 text-black text-[14px] font-medium px-6 py-3 hover:bg-gray-50 transition-colors"
            >
              Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  );
};

export default AdminGate;
